import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import {
  ORDER_STATUS_OPTIONS,
  FULFILLMENT_STATUS_OPTIONS,
  ORDER_SORT_OPTIONS,
  OPTIONAL_TABLE_COLUMNS,
  OPTIONAL_TABLE_COLUMN_LABELS,
  PDF_FIELD_OPTIONS,
  PDF_FIELD_LABELS,
  getStatusMeta,
  getFulfillmentStatusMeta,
  getPreparationItemKey,
  toArrayValue,
  toCurrency,
} from "./shopUtils";

export default function ShopOrdersFiltersBar({
  // Filters
  adminMonth,
  setAdminMonth,
  orderStatusFilter,
  setOrderStatusFilter,
  normalizedStatusFilter,
  fulfillmentStatusFilter,
  setFulfillmentStatusFilter,
  searchTerm,
  setSearchTerm,
  sortBy,
  setSortBy,
  // Advanced
  optionalTableColumns,
  setOptionalTableColumns,
  processingScope,
  setProcessingScope,
  pdfFields,
  setPdfFields,
  onlyOpenForPreparation,
  setOnlyOpenForPreparation,
  // Selection
  allOrdersSelected,
  partiallySelected,
  onToggleSelectAll,
  selectedOrderIds,
  selectedOrdersTotal,
  exportableSelectedOrderIds,
  cancelableSelectedOrderIds,
  // Actions
  onExportSepa,
  exportingSepa,
  onBulkActionSubmit,
  onPrintOrdersPdf,
  onPrintPreparationPdf,
  runningAdminAction,
  onRefetch,
  // Display data
  visibleOrders,
  selectedOrders,
  adminOrders,
  processingOrders,
  // Preparation
  preparationOrders,
  preparationSummary,
  sortedPreparationItems,
  preparationSortField,
  preparationSortDirection,
  handlePreparationSort,
  selectedPreparationKeySet,
  handleTogglePreparationSelection,
  handleTogglePreparationSelectAll,
  allPreparationItemsSelected,
  partiallySelectedPreparationItems,
  visibleSelectedPreparationCount,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState("");

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2.5 }}
    >
      <Stack spacing={2}>
        {/* ── Primary filters ── */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.5}
          useFlexGap
          flexWrap="wrap"
        >
          <TextField
            type="month"
            label="Monat"
            value={adminMonth}
            onChange={(e) => setAdminMonth(e.target.value)}
            sx={{ width: { xs: "100%", lg: 220 } }}
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={ORDER_STATUS_OPTIONS}
            value={normalizedStatusFilter}
            limitTags={2}
            onChange={(_, values) => {
              const unique = Array.from(new Set(values));
              setOrderStatusFilter(
                unique.filter((s) => ORDER_STATUS_OPTIONS.includes(s)),
              );
            }}
            getOptionLabel={(status) => getStatusMeta(status).label}
            renderTags={(value, getTagProps) =>
              value.map((status, index) => {
                const meta = getStatusMeta(status);
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={status}
                    size="small"
                    variant="outlined"
                    color={meta.color}
                    label={meta.label}
                  />
                );
              })
            }
            renderOption={(props, status, { selected }) => {
              const meta = getStatusMeta(status);
              return (
                <li {...props}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                  <Typography variant="body2">{meta.label}</Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Statusfilter"
                placeholder="Status wählen"
              />
            )}
            sx={{ minWidth: { xs: "100%", lg: 320 } }}
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={FULFILLMENT_STATUS_OPTIONS}
            value={fulfillmentStatusFilter}
            limitTags={2}
            onChange={(_, values) => setFulfillmentStatusFilter(values)}
            getOptionLabel={(status) =>
              getFulfillmentStatusMeta(status).label
            }
            renderTags={(value, getTagProps) =>
              value.map((status, index) => {
                const meta = getFulfillmentStatusMeta(status);
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={status}
                    size="small"
                    variant="outlined"
                    color={meta.color}
                    label={meta.label}
                  />
                );
              })
            }
            renderOption={(props, status, { selected }) => {
              const meta = getFulfillmentStatusMeta(status);
              return (
                <li {...props}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                  <Typography variant="body2">{meta.label}</Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Versandstatus"
                placeholder="Versandstatus wählen"
              />
            )}
            sx={{ minWidth: { xs: "100%", lg: 260 } }}
          />
          <TextField
            label="Suche"
            placeholder="Mitglied, Produkt, Bestell-ID, E-Mail, Adresse..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: { xs: "100%", lg: 320 }, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={18} height={18} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: { xs: "100%", lg: 260 } }}>
            <InputLabel>Sortierung</InputLabel>
            <Select
              label="Sortierung"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {ORDER_SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={onRefetch}
            startIcon={
              <Iconify icon="eva:refresh-fill" width={18} height={18} />
            }
          >
            Aktualisieren
          </Button>
        </Stack>

        {/* ── Bulk actions ── */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={allOrdersSelected}
                indeterminate={partiallySelected}
                onChange={(e) => onToggleSelectAll(e.target.checked)}
              />
            }
            label="Alle sichtbaren auswählen"
          />
          <Chip
            variant="outlined"
            label={`${selectedOrderIds.length} ausgewählt · ${toCurrency(
              selectedOrdersTotal,
            )}`}
          />
          <FormControl
            size="small"
            sx={{ minWidth: { xs: "100%", sm: 260 } }}
          >
            <InputLabel>Aktion für Auswahl</InputLabel>
            <Select
              label="Aktion für Auswahl"
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              disabled={!selectedOrderIds.length || runningAdminAction}
            >
              <ListSubheader>Status ändern auf</ListSubheader>
              {ORDER_STATUS_OPTIONS.map((status) => {
                const meta = getStatusMeta(status);
                return (
                  <MenuItem key={`status-${status}`} value={`status:${status}`}>
                    <Chip
                      size="small"
                      color={meta.color}
                      label={meta.label}
                      sx={{ pointerEvents: "none" }}
                    />
                  </MenuItem>
                );
              })}
              <Divider />
              <MenuItem value="delete">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Iconify icon="eva:trash-2-outline" width={16} color="error.main" />
                  <Typography variant="body2" color="error.main">
                    Löschen
                  </Typography>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            disabled={!bulkAction || !selectedOrderIds.length || runningAdminAction}
            onClick={() => {
              onBulkActionSubmit(bulkAction);
              setBulkAction("");
            }}
            startIcon={<Iconify icon="mdi:check" width={18} />}
          >
            Ausführen
          </Button>
        </Stack>

        {/* ── SEPA info alert ── */}
        {selectedOrderIds.length > 0 &&
        exportableSelectedOrderIds.length !== selectedOrderIds.length ? (
          <Alert severity="info">
            Nur Bestellungen mit Status <strong>Neu</strong>,{" "}
            <strong>In Bearbeitung</strong> oder{" "}
            <strong>Freigegeben</strong> werden beim SEPA-Export
            berücksichtigt.
          </Alert>
        ) : null}

        {processingScope === "selected" && selectedOrders.length === 0 ? (
          <Alert severity="info">
            Datenbasis &bdquo;Ausgewählte Bestellungen&ldquo; ist aktiv, aber
            es wurden noch keine Bestellungen markiert.
          </Alert>
        ) : null}

        {/* ── Advanced toggle ── */}
        <Button
          size="small"
          variant="text"
          onClick={() => setAdvancedOpen((prev) => !prev)}
          startIcon={
            <Iconify
              icon={
                advancedOpen
                  ? "eva:chevron-up-fill"
                  : "eva:chevron-down-fill"
              }
              width={18}
            />
          }
          sx={{ alignSelf: "flex-start" }}
        >
          {advancedOpen
            ? "Erweiterte Optionen ausblenden"
            : "Erweiterte Optionen & Kommissionierung"}
        </Button>

        <Collapse in={advancedOpen} timeout="auto" unmountOnExit>
          <Stack spacing={2.5}>
            {/* ── Advanced controls row ── */}
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={1.5}
              useFlexGap
              flexWrap="wrap"
            >
              <FormControl sx={{ minWidth: { xs: "100%", lg: 260 } }}>
                <InputLabel>Zusätzliche Spalten</InputLabel>
                <Select
                  multiple
                  label="Zusätzliche Spalten"
                  value={optionalTableColumns}
                  onChange={(e) =>
                    setOptionalTableColumns(toArrayValue(e.target.value))
                  }
                  renderValue={(selected) =>
                    selected.length
                      ? selected
                          .map(
                            (key) => OPTIONAL_TABLE_COLUMN_LABELS[key] || key,
                          )
                          .join(", ")
                      : "Nur Standardspalten"
                  }
                >
                  {OPTIONAL_TABLE_COLUMNS.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      <Checkbox
                        checked={optionalTableColumns.includes(option.key)}
                      />
                      <Typography variant="body2">{option.label}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: { xs: "100%", lg: 260 } }}>
                <InputLabel>Datenbasis</InputLabel>
                <Select
                  label="Datenbasis"
                  value={processingScope}
                  onChange={(e) => setProcessingScope(e.target.value)}
                >
                  <MenuItem value="visible">
                    Sichtbare Bestellungen ({visibleOrders.length})
                  </MenuItem>
                  <MenuItem value="selected">
                    Ausgewählte Bestellungen ({selectedOrders.length})
                  </MenuItem>
                  <MenuItem value="all">
                    Alle geladenen Bestellungen ({adminOrders.length})
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: { xs: "100%", lg: 300 } }}>
                <InputLabel>PDF-Felder</InputLabel>
                <Select
                  multiple
                  label="PDF-Felder"
                  value={pdfFields}
                  onChange={(e) =>
                    setPdfFields(toArrayValue(e.target.value))
                  }
                  renderValue={(selected) =>
                    selected.length
                      ? selected
                          .map((key) => PDF_FIELD_LABELS[key] || key)
                          .join(", ")
                      : "Standardfelder"
                  }
                >
                  {PDF_FIELD_OPTIONS.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      <Checkbox checked={pdfFields.includes(option.key)} />
                      <Typography variant="body2">{option.label}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={onlyOpenForPreparation}
                    onChange={(e) =>
                      setOnlyOpenForPreparation(e.target.checked)
                    }
                  />
                }
                label="Kommissionierung nur Neu/In Bearbeitung/Freigegeben"
              />
            </Stack>

            {/* ── Print & export actions ── */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              useFlexGap
              flexWrap="wrap"
            >
              <Tooltip title='Erstellt eine SEPA-XML-Datei. Exportierte Bestellungen werden automatisch als "Erfüllt" markiert.'>
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={
                      !exportableSelectedOrderIds.length ||
                      exportingSepa ||
                      runningAdminAction
                    }
                    onClick={() =>
                      onExportSepa(exportableSelectedOrderIds)
                    }
                    startIcon={
                      <Iconify
                        icon="mdi:file-xml-box"
                        width={18}
                        height={18}
                      />
                    }
                  >
                    SEPA Export
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Druckt die ausgewählte Bestellübersicht mit frei wählbaren Feldern als PDF.">
                <span>
                  <Button
                    variant="contained"
                    color="info"
                    disabled={!processingOrders.length}
                    onClick={onPrintOrdersPdf}
                    startIcon={
                      <Iconify
                        icon="mdi:file-pdf-box"
                        width={18}
                        height={18}
                      />
                    }
                  >
                    Bestell-PDF drucken
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Druckt die gruppierte Produkt-Aufsummierung für das Lager/Kommissionieren als PDF.">
                <span>
                  <Button
                    variant="outlined"
                    disabled={!preparationSummary.items.length}
                    onClick={onPrintPreparationPdf}
                    startIcon={
                      <Iconify
                        icon="mdi:clipboard-list-outline"
                        width={18}
                        height={18}
                      />
                    }
                  >
                    Kommissionier-PDF
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            <Divider />

            {/* ── Preparation / picking summary ── */}
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Box>
                  <Typography variant="h6">
                    Kommissionierübersicht
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gruppierte Produktmengen aus der gewählten Datenbasis für
                    eine schnelle Lager-Vorbereitung.
                  </Typography>
                </Box>
                <Chip
                  variant="outlined"
                  label={`${preparationSummary.items.length} Produktvarianten · ${visibleSelectedPreparationCount} markiert`}
                />
              </Stack>

              <Grid container spacing={1.5}>
                <Grid item xs={6} md={3}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Bestellungen
                    </Typography>
                    <Typography variant="h6">
                      {preparationOrders.length}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Menge gesamt
                    </Typography>
                    <Typography variant="h6">
                      {preparationSummary.totalQuantity}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Varianten
                    </Typography>
                    <Typography variant="h6">
                      {preparationSummary.items.length}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Umsatz
                    </Typography>
                    <Typography variant="h6">
                      {toCurrency(preparationSummary.totalRevenue)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {preparationSummary.items.length > 0 ? (
                <Paper
                  variant="outlined"
                  sx={{ borderRadius: 2, overflow: "hidden" }}
                >
                  <TableContainer sx={{ maxHeight: 340 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={allPreparationItemsSelected}
                              indeterminate={
                                partiallySelectedPreparationItems
                              }
                              onChange={(e) =>
                                handleTogglePreparationSelectAll(
                                  e.target.checked,
                                )
                              }
                            />
                          </TableCell>
                          {[
                            {
                              field: "productName",
                              label: "Produkt",
                              defaultDir: "asc",
                            },
                            {
                              field: "variantLabel",
                              label: "Variante",
                              defaultDir: "asc",
                            },
                            {
                              field: "quantity",
                              label: "Menge",
                              align: "right",
                              defaultDir: "desc",
                            },
                            {
                              field: "orderCount",
                              label: "Bestellungen",
                              align: "right",
                              defaultDir: "desc",
                            },
                            {
                              field: "revenue",
                              label: "Umsatz",
                              align: "right",
                              defaultDir: "desc",
                            },
                          ].map((col) => (
                            <TableCell
                              key={col.field}
                              align={col.align}
                              sortDirection={
                                preparationSortField === col.field
                                  ? preparationSortDirection
                                  : false
                              }
                            >
                              <TableSortLabel
                                active={
                                  preparationSortField === col.field
                                }
                                direction={
                                  preparationSortField === col.field
                                    ? preparationSortDirection
                                    : col.defaultDir
                                }
                                onClick={() =>
                                  handlePreparationSort(col.field)
                                }
                              >
                                {col.label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedPreparationItems.map((item) => {
                          const itemKey = getPreparationItemKey(item);
                          const isSelected =
                            selectedPreparationKeySet.has(itemKey);
                          return (
                            <TableRow
                              key={itemKey}
                              hover
                              selected={isSelected}
                              onClick={() =>
                                handleTogglePreparationSelection(
                                  itemKey,
                                  !isSelected,
                                )
                              }
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell
                                padding="checkbox"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) =>
                                    handleTogglePreparationSelection(
                                      itemKey,
                                      e.target.checked,
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                >
                                  {item.productName}
                                </Typography>
                              </TableCell>
                              <TableCell>{item.variantLabel}</TableCell>
                              <TableCell align="right">
                                {item.quantity}
                              </TableCell>
                              <TableCell align="right">
                                {item.orderCount}
                              </TableCell>
                              <TableCell align="right">
                                {toCurrency(item.revenue)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : (
                <Alert severity="info">
                  Für die aktuelle Datenbasis sind keine Produktpositionen
                  vorhanden.
                </Alert>
              )}
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
